import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useListPlansQuery, useSelectPlanMutation, Plan } from '@/redux/internal-services/onboardingApi';
import { setSubscriptionStatus } from '@/redux/slices/sessionStateSlice';
import { useAppSelector } from '@/hooks';
import LeftSidePanel from '@/components/UI/LeftSidePanel';
import { getErrorDetail } from '@/utils/apiError';
import { requestOnboardingVoiceMode, setOnboardingVoiceMode } from './onboardingVoiceMode';

const PlanCard: React.FC<{
  plan: Plan;
  selected: boolean;
  billingCycle: 'monthly' | 'annual';
  busy: boolean;
  onSelect: () => void;
  onGetStarted: () => void;
}> = ({ plan, selected, billingCycle, busy, onSelect, onGetStarted }) => {
  const price = billingCycle === 'annual' ? plan.price_annual : plan.price_monthly;
  const isPopular = (plan as Plan & { is_popular?: boolean }).is_popular;
  // Only the currently selected card is rendered dark + elevated — single selection.
  const dark = selected;

  return (
    <div
      className={`relative h-full rounded-2xl p-6 cursor-pointer transition-all flex flex-col ${
        dark
          ? 'bg-brand-dark text-white shadow-2xl lg:scale-105 z-10'
          : 'bg-ui-card text-ui-text border border-ui-border shadow-sm hover:shadow-md'
      }`}
      onClick={onSelect}
    >
      {isPopular && (
        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] tracking-wide font-bold px-3 py-1 rounded-full whitespace-nowrap ${dark ? 'bg-ui-card text-brand-dark' : 'bg-brand-dark text-white'}`}>
          MOST POPULAR
        </div>
      )}

      <div className="mb-4">
        <p className={`text-4xl font-bold ${dark ? 'text-white' : 'text-ui-text'}`}>
          ${price ?? 0}
          <span className={`text-base font-normal ${dark ? 'text-white/70' : 'text-ui-text2'}`}>{billingCycle === 'annual' ? '/yr' : '/mo'}</span>
        </p>
        <h3 className={`text-lg font-semibold mt-2 ${dark ? 'text-white' : 'text-ui-text'}`}>{plan.name}</h3>
        <p className={`text-sm mt-1 ${dark ? 'text-white/70' : 'text-ui-text2'}`}>{plan.description}</p>
      </div>

      {plan.features && plan.features.length > 0 && (
        <ul className="space-y-2.5 flex-1">
          {plan.features.map((f) => (
            <li key={f} className="flex items-center gap-2.5 text-sm">
              <span
                className={`flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0 ${
                  dark ? 'bg-white/20 text-white' : 'bg-brand-light text-brand-dark'
                }`}
              >
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </span>
              <span className={dark ? 'text-white/90' : 'text-ui-text2'}>{f}</span>
            </li>
          ))}
        </ul>
      )}

      <button
        onClick={(e) => { e.stopPropagation(); onGetStarted(); }}
        disabled={busy}
        className={`mt-6 w-full py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-60 ${
          dark ? 'bg-white dark:bg-white/90 text-brand-dark hover:bg-gray-100 dark:hover:bg-white/80' : 'bg-brand-dark text-white hover:opacity-90'
        }`}
      >
        Get Started
      </button>
    </div>
  );
};

const PlanSelectionPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data, isLoading } = useListPlansQuery();
  const [selectPlan, { isLoading: isSelecting }] = useSelectPlanMutation();

  // Once SAM welcome has fired for this user, plan selection is off-limits — a returning
  // member who lands here by URL or stale link must go straight to /chat.
  const samWelcomeShown = useAppSelector((s) => s.sessionState.samWelcomeShown);
  useEffect(() => {
    if (samWelcomeShown) navigate('/chat', { replace: true });
  }, [samWelcomeShown, navigate]);

  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [error, setError] = useState('');

  const plans = data?.data ?? [];

  // Default the selection to the popular plan (falling back to the first) once plans arrive.
  useEffect(() => {
    if (!selectedPlanId && plans.length) {
      const popular = plans.find((p) => (p as Plan & { is_popular?: boolean }).is_popular);
      setSelectedPlanId((popular ?? plans[0]).plan_id);
    }
  }, [plans, selectedPlanId]);

  const activate = async (planId: string, cycle: 'monthly' | 'annual') => {
    await selectPlan({ plan_id: planId, billing_cycle: cycle }).unwrap();
    dispatch(setSubscriptionStatus('active'));
    // Ask for the mic now that the plan is chosen: granted → SAM guides the setup by voice;
    // denied/unsupported → the setup wizard falls back to the manual form.
    setOnboardingVoiceMode(await requestOnboardingVoiceMode());
    navigate('/onboarding/setup');
  };

  // Clicking a card only selects it (brings it forward) — it does not activate the plan.
  const handleSelect = (planId: string) => {
    setSelectedPlanId(planId);
    setError('');
  };

  // Activation happens only via the "Get Started" button on a card.
  const handleGetStarted = async (planId: string) => {
    setSelectedPlanId(planId);
    setError('');
    try {
      await activate(planId, billingCycle);
    } catch (err: unknown) {
      setError(getErrorDetail(err) ?? t('common.error'));
    }
  };

  // "Skip this step" — start the 14-day free trial on the trial-eligible plan so the user can
  // explore now and switch plans later from Settings. Routing requires an activated subscription,
  // so we activate the trial plan rather than navigating with no subscription (which bounces back).
  const handleSkip = async () => {
    setError('');
    const trialPlan = plans.find((p) => p.trial_eligible) ?? plans[0];
    if (!trialPlan) { navigate('/onboarding/setup'); return; }
    try {
      await activate(trialPlan.plan_id, billingCycle);
    } catch (err: unknown) {
      setError(getErrorDetail(err) ?? t('common.error'));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-white text-lg">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-ui-raised">
      {/* Left panel — shared app-wide branded side panel */}
      <LeftSidePanel />

      {/* Right panel — light, plan selection */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <h1 className="text-2xl font-bold text-ui-text mb-7">Choose what works for your team</h1>

        {/* Billing toggle */}
        <div className="flex bg-ui-subtle rounded-full p-1 mb-10">
          {(['monthly', 'annual'] as const).map((cycle) => (
            <button
              key={cycle}
              onClick={() => setBillingCycle(cycle)}
              className={`px-6 py-1.5 rounded-full text-sm font-medium transition capitalize ${
                billingCycle === cycle ? 'bg-brand-dark text-white shadow' : 'text-ui-text2 hover:text-ui-text'
              }`}
            >
              {cycle === 'annual' ? 'Yearly' : 'Monthly'}
            </button>
          ))}
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 w-full max-w-4xl items-stretch">
          {plans.map((plan) => (
            <PlanCard
              key={plan.plan_id}
              plan={plan}
              selected={selectedPlanId === plan.plan_id}
              billingCycle={billingCycle}
              busy={isSelecting}
              onSelect={() => handleSelect(plan.plan_id)}
              onGetStarted={() => handleGetStarted(plan.plan_id)}
            />
          ))}
        </div>

        {error && <p className="text-danger text-sm mt-4">{error}</p>}

        <button
          onClick={handleSkip}
          disabled={isSelecting}
          className="mt-8 text-xs font-semibold tracking-wide text-ui-text3 hover:text-gray-600 transition uppercase disabled:opacity-60"
        >
          Skip this step
        </button>
      </div>
    </div>
  );
};

export default PlanSelectionPage;
